import { FunctionComponent } from "react";

type Option = {
  name?: string;
  value?: string;
  [key: string]: any;
};

type Props = {
  radios: Option[];
  value?: string | number;
  onChange?: (e: React.FormEvent<HTMLInputElement>, option?: Option) => void;
  name: string;
  renderContent: (option: Option, isSelected: boolean) => React.ReactNode;
  errorMsg?: string;
  wrapperClassName?: string;
  optionClassName?: string;
  onClickSelected?: () => void;
};

export const FormRadioGroup: FunctionComponent<Props> = ({
  radios,
  value = "",
  onChange,
  name,
  renderContent,
  errorMsg = "",
  wrapperClassName = "",
  optionClassName = "",
  onClickSelected = () => {},
}) => {
  const hasError = !!errorMsg;
  return (
    <>
      <div className={`flex flex-col gap-3 ${wrapperClassName}`}>
        {radios.map((option) => {
          const isSelected = option.value == value || option.name == value;
          const id = option.name || `${name}-${option.value}`;
          const baseBorder = isSelected
            ? "#358639"
            : hasError
            ? "#dc2626"
            : "#d1d5db";
          const bg = isSelected ? "#f0f9f1" : "#ffffff";
          const shadow = isSelected
            ? "0 4px 12px rgba(53, 134, 57, 0.12)"
            : "0 1px 2px rgba(0,0,0,0.04)";
          return (
            <div key={`${option.name || ""}-${option.value || ""}`}>
              <input
                type="radio"
                name={name}
                id={id}
                checked={isSelected}
                onChange={() => {}}
                onClick={(e) => {
                  if (isSelected) {
                    onClickSelected();
                  } else {
                    onChange && onChange(e, option);
                  }
                }}
                value={option.value || option.name}
                className="sr-only"
              />
              <label
                htmlFor={id}
                className={`block cursor-pointer rounded-xl transition-all ${optionClassName}`}
                style={{
                  border: `2px solid ${baseBorder}`,
                  backgroundColor: bg,
                  boxShadow: shadow,
                }}
              >
                <div className="relative">
                  {renderContent(option, isSelected)}
                  <div
                    className="absolute top-4 right-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors"
                    style={{
                      border: `2px solid ${
                        isSelected ? "#358639" : "#cbd5e1"
                      }`,
                      backgroundColor: isSelected ? "#358639" : "#ffffff",
                    }}
                    aria-hidden="true"
                  >
                    {isSelected && (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
              </label>
            </div>
          );
        })}
      </div>
      {hasError && (
        <div
          className="mt-3 flex items-start gap-2 p-3 rounded-lg"
          style={{
            backgroundColor: "#fef2f2",
            border: "1px solid #fecaca",
          }}
          role="alert"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#dc2626"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0, marginTop: "1px" }}
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
          <p className="text-sm font-medium" style={{ color: "#dc2626" }}>
            {errorMsg}
          </p>
        </div>
      )}
    </>
  );
};

export default FormRadioGroup;
